import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import axios from "axios";

const MOBILE_BREAKPOINT = 768;
const API_URL = process.env.REACT_APP_API_URL || "";

// crm-sidebar-v3 (menú con grupos colapsables)
export default function Sidebar({ isOpen, onToggle }) {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role;
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [modulosConfig, setModulosConfig] = useState({});
  const [openGroups, setOpenGroups] = useState({});

  const handleResize = useCallback(() => {
    const mobile = window.innerWidth < MOBILE_BREAKPOINT;
    setIsMobile(mobile);
    if (!mobile && !isOpen) onToggle(true);
  }, [isOpen, onToggle]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (isMobile) onToggle(false);
  }, [location.pathname, isMobile, onToggle]);

  // Cargar configuración de módulos
  useEffect(() => {
    if (role === "ADMINISTRADOR" || role === "CAJERO") {
      const token = localStorage.getItem("token");
      if (token) {
        axios.get(`${API_URL}/api/configuracion/contable`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          setModulosConfig(res.data || {});
        }).catch(() => {});
      }
    }
  }, [role]);

  const isActive = (path) => location.pathname === path;
  const m = modulosConfig;

  // ── Definición del menú por rol (grupos colapsables) ─────────────────────
  // Cada item: { to, label, icon, show? , disabled? }
  // Cada grupo: { id, title, icon, items: [...] }
  const menu = useMemo(() => {
    if (role === "SUPER_ADMIN") {
      return {
        topLinks: [],
        groups: [
          { id: "admin", title: "Administración", icon: "🏢", items: [
            { to: "/admin/empresas", label: "Gestión Empresas", icon: "🏢" },
          ]},
        ],
      };
    }
    if (role === "CAJERO") {
      return {
        topLinks: [],
        groups: [
          { id: "vender", title: "Vender", icon: "🧾", items: [
            { to: "/pos", label: "Punto de Venta (POS)", icon: "🖥️", show: !!m.pos_habilitado },
            { to: "/pos/arqueo-caja", label: "Arqueo de Caja", icon: "🧮", show: !!m.arqueo_caja_habilitado },
            { to: "/pos/recibos", label: "Recibos de Venta", icon: "🧾", show: !!m.recibos_sin_factura_habilitado },
            { to: "/crear-factura", label: "Crear Factura", icon: "📄" },
            { to: "/facturaslist", label: "Facturas", icon: "📑" },
            { to: "/clientes", label: "Clientes", icon: "👥" },
          ]},
          { id: "crm", title: "CRM", icon: "💬", items: [
            { to: "/crm/bandeja", label: "Bandeja de Mensajes", icon: "💬" },
          ]},
          { id: "ajustes", title: "Ajustes", icon: "🎨", items: [
            { to: "/pos/impresora", label: "Tema / Color", icon: "🎨" },
          ]},
        ],
      };
    }
    // ADMINISTRADOR
    return {
      topLinks: [
        { to: "/", label: "Inicio", icon: "🏠" },
      ],
      groups: [
        { id: "vender", title: "Vender", icon: "🧾", items: [
          { to: "/pos", label: "Punto de Venta (POS)", icon: "🖥️", show: !!m.pos_habilitado },
          { to: "/pos/arqueo-caja", label: "Arqueo de Caja", icon: "🧮", show: !!m.arqueo_caja_habilitado },
          { to: "/pos/recibos", label: "Recibos de Venta", icon: "🧾", show: !!m.recibos_sin_factura_habilitado },
          { to: "/crear-factura", label: "Crear Factura", icon: "🧾" },
          { to: "/facturaslist", label: "Facturas", icon: "📑" },
          { to: "/contabilidad/notas-credito", label: "Nota de Crédito", icon: "📄" },
          { to: "/contabilidad/guia-remision", label: "Guía de Remisión", icon: "🚚" },
          { to: "/clientes", label: "Clientes", icon: "👥" },
          { to: "/cotizaciones", label: "Cotizar", icon: "📋" },
        ]},
        { id: "catalogo", title: "Catálogo", icon: "📦", items: [
          { to: "/inventario/productos", label: "Productos", icon: "📦" },
          { to: "/servicios", label: "Servicios", icon: "🔧" },
          { to: "/inventario/bodegas", label: "Bodegas", icon: "🏭" },
          { to: "/inventario/ingresos-egresos", label: "Ingresos/Egresos", icon: "↕️" },
          { to: "/inventario/transferencias", label: "Transferencias", icon: "🔄" },
          { to: "/inventario/kardex", label: "Kardex", icon: "📒" },
          { to: "/inventario/importar-csv", label: "Importar CSV", icon: "📤" },
        ]},
        { id: "compras", title: "Compras", icon: "🛒", items: [
          { to: "/compras", label: "Nueva Compra", icon: "🛒" },
          { to: "/compras-manual", label: "Compra Manual", icon: "📝" },
          { to: "/contabilidad/liquidacion-compras", label: "Liquidación de Compra", icon: "📋" },
          { to: "/proveedores", label: "Proveedores", icon: "🏭" },
          { to: "/contabilidad/retenciones", label: "Retenciones", icon: "📋", show: !!m.retenciones_habilitadas },
        ]},
        { id: "reportes", title: "Reportes", icon: "📊", items: [
          { to: "/contabilidad/reportes", label: "Reportes Contables", icon: "📊" },
          { to: "/contabilidad/libro-diario", label: "Libro Diario", icon: "📖" },
          { to: "/contabilidad/ats", label: "ATS (Anexo SRI)", icon: "📋" },
        ]},
        { id: "finanzas", title: "Finanzas", icon: "🏦", items: [
          { to: "/cuentas-cobrar", label: "Cuentas por Cobrar", icon: "💰" },
          { to: "/cuentas-pagar", label: "Cuentas por Pagar", icon: "💸" },
          { to: "/tesoreria", label: "Tesorería", icon: "🏦", show: !!m.tesoreria_habilitada },
          { to: "/tesoreria/conciliacion", label: "Conciliación Bancaria", icon: "⚖️", show: !!m.tesoreria_habilitada },
        ]},
        { id: "crm", title: "CRM", icon: "📈", items: [
          { to: "/crm/pipeline", label: "Pipeline de Ventas", icon: "🔥" },
          { to: "/crm/bandeja", label: "Bandeja de Mensajes", icon: "💬" },
          { to: "/crm/contactos", label: "Contactos / Leads", icon: "🤝" },
        ]},
        { id: "config", title: "Configuración", icon: "⚙️", items: [
          { to: "/contabilidad/centro-costos", label: "Centro de Costos", icon: "🏗️", show: !!m.centro_costos_habilitado },
          { to: "/configuracion", label: "Mi Empresa", icon: "🏢" },
          { to: "/configuracion/usuarios", label: "Usuarios / Cajeros", icon: "👥" },
          { to: "/contabilidad/configuracion", label: "Contabilidad", icon: "⚙️" },
          { to: "/configuracion/email", label: "Email - SMTP", icon: "📧" },
          { to: "/configuracion/whatsapp", label: "WhatsApp Business", icon: "📱" },
          { to: "/configuracion/chat-widget", label: "Chat Widget", icon: "🔌" },
          { to: "/configuracion/asistente-ia", label: "Asistente IA", icon: "🤖" },
          { to: "/pos/impresora", label: "Impresora / POS", icon: "🖨️", show: !!m.pos_habilitado },
        ]},
      ],
    };
  }, [role, m]);

  // Auto-abrir SOLO el grupo que contiene la ruta activa (acordeón)
  useEffect(() => {
    const activo = menu.groups.find(g => (g.items || []).some(it => it.to === location.pathname));
    if (activo) setOpenGroups(prev => (prev[activo.id] ? prev : { [activo.id]: true }));
  }, [location.pathname, menu]);

  // Acordeón: al abrir un grupo se cierran los demás
  const toggleGroup = (id) => setOpenGroups(prev => (prev[id] ? {} : { [id]: true }));

  const renderItem = (it, dentroDeGrupo = true) => {
    if (it.show === false) return null;
    // Los subítems de un grupo van sin ícono (solo texto); los enlaces top sí llevan ícono
    const padL = dentroDeGrupo ? 42 : 20;
    if (it.disabled) {
      return (
        <span key={it.to} style={{ ...itemStyle, paddingLeft: padL, opacity: 0.35, cursor: "not-allowed", pointerEvents: "none" }}>
          {!dentroDeGrupo && <span style={iconStyle}>{it.icon}</span>}{it.label}
          <span style={{ marginLeft: "auto", fontSize: 9, background: "#333", padding: "2px 6px", borderRadius: 10, color: "#777" }}>Próx.</span>
        </span>
      );
    }
    const active = isActive(it.to);
    return (
      <Link key={it.to} to={it.to} style={{
        ...itemStyle,
        paddingLeft: padL,
        fontWeight: active ? 600 : 400,
        color: active ? "#FF7A00" : "#b9c0cf",
      }}>
        {!dentroDeGrupo && <span style={iconStyle}>{it.icon}</span>}{it.label}
      </Link>
    );
  };

  const renderGroup = (g) => {
    const items = (g.items || []).filter(it => it.show !== false);
    if (items.length === 0) return null;
    const abierto = !!openGroups[g.id];
    const tieneActivo = items.some(it => it.to === location.pathname);
    return (
      <div key={g.id}>
        <button onClick={() => toggleGroup(g.id)} style={{
          ...groupHeaderStyle,
          color: tieneActivo ? "#FF7A00" : "#cbd2e0",
        }}>
          <span style={iconStyle}>{g.icon}</span>
          <span style={{ flex: 1, textAlign: "left" }}>{g.title}</span>
          <span style={{
            fontSize: 11, color: "#5a6678", lineHeight: 1,
            transition: "transform .2s ease",
            transform: abierto ? "rotate(180deg)" : "rotate(0deg)",
          }}>⌄</span>
        </button>
        <div style={{
          overflow: "hidden",
          maxHeight: abierto ? items.length * 44 + 8 : 0,
          transition: "max-height .25s ease",
        }}>
          {items.map(it => renderItem(it, true))}
        </div>
      </div>
    );
  };

  return (
    <>
      {isMobile && isOpen && (
        <div onClick={() => onToggle(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 998 }} />
      )}

      <aside style={{
        width: 240,
        background: "#101728",
        color: "#e0e0e0",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 999,
        transform: isOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.3s ease",
        overflowY: "auto",
        overflowX: "hidden",
      }}>
        {/* Header */}
        <div style={{
          height: 60, boxSizing: "border-box",
          padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid #1e2a44", flexShrink: 0,
        }}>
          <img src="/logo-menu-sidebar-ai-leth-blanco.png" alt="Aileth"
            style={{ maxWidth: 120, height: "auto" }}
            onError={(e) => { e.target.onerror = null; e.target.src = "/logo-aileth-navar.png"; }}
          />
          {isMobile && (
            <button onClick={() => onToggle(false)} style={{
              background: "none", border: "none", color: "#999", fontSize: 20, cursor: "pointer",
            }}>✕</button>
          )}
        </div>

        {/* Navegación */}
        <nav style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, paddingBottom: 16, paddingTop: 10 }}>
          {menu.topLinks.map(it => renderItem(it, false))}
          {menu.topLinks.length > 0 && <div style={{ height: 6 }} />}
          {menu.groups.map(renderGroup)}
        </nav>

      </aside>
    </>
  );
}

// Encabezado de grupo (clicable, colapsable)
const groupHeaderStyle = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  padding: "11px 18px",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: 13.5,
  fontWeight: 600,
  color: "#cbd2e0",
  textAlign: "left",
  transition: "color .15s",
};

// Item de menú (subítem dentro de un grupo)
const itemStyle = {
  display: "flex",
  alignItems: "center",
  padding: "9px 18px 9px 30px",
  textDecoration: "none",
  color: "#b9c0cf",
  fontSize: 13,
  transition: "all 0.15s",
};

const iconStyle = { marginRight: 10, fontSize: 15, width: 20, textAlign: "center", flexShrink: 0 };
